import { ref, watchEffect, type Ref, isRef } from 'vue'
import { ApiError } from '@/lib/api'

interface UseApiReturn<T> {
  data: Ref<T | null>
  error: Ref<ApiError | null>
  loading: Ref<boolean>
  refresh: () => Promise<void>
}

/**
 * Runs fetches for one composable, newest request wins. Requests outlive the
 * state that started them — switching project fires the next fetch before the
 * previous lands — so a superseded response is discarded rather than allowed to
 * overwrite fresher data or paint its error over a healthy page.
 */
function createRunner<T>(
  data: Ref<T | null>,
  error: Ref<ApiError | null>,
  loading: Ref<boolean>,
) {
  let requestId = 0

  return async function run(fetcher: () => Promise<T>) {
    const id = ++requestId
    loading.value = true
    error.value = null
    try {
      const result = await fetcher()
      if (id === requestId) data.value = result
    } catch (e) {
      if (id !== requestId) return
      error.value = e instanceof ApiError ? e : new ApiError(0, (e as Error).message)
    } finally {
      if (id === requestId) loading.value = false
    }
  }
}

export function useApi<T>(
  fetcher: () => Promise<T>,
  options?: { immediate?: boolean },
): UseApiReturn<T> {
  const data = ref<T | null>(null) as Ref<T | null>
  const error = ref<ApiError | null>(null)
  const loading = ref(false)

  const run = createRunner(data, error, loading)
  const refresh = () => run(fetcher)

  if (options?.immediate ?? true) {
    refresh()
  }

  return { data, error, loading, refresh }
}

/**
 * Reactive version that re-fetches when dependencies change.
 * Pass a reactive getter that returns the fetcher.
 */
export function useReactiveApi<T>(
  fetcherGetter: () => (() => Promise<T>) | null,
): UseApiReturn<T> {
  const data = ref<T | null>(null) as Ref<T | null>
  const error = ref<ApiError | null>(null)
  const loading = ref(false)

  const run = createRunner(data, error, loading)
  let currentFetcher: (() => Promise<T>) | null = null

  async function refresh() {
    if (currentFetcher) await run(currentFetcher)
  }

  watchEffect(() => {
    currentFetcher = fetcherGetter()
    if (!currentFetcher) return
    // Only re-runs when a dependency changed, so whatever is in `data` belongs
    // to the previous key. Drop it rather than render another project's rows
    // until the new response lands.
    data.value = null
    refresh()
  })

  return { data, error, loading, refresh }
}

// Convenience composables for common API calls
import { api } from '@/lib/api'
import type {
  Project,
  ProjectTree,
  FlagWithEnvironments,
  Environment,
  Segment,
  SyncStatus,
  Schedule,
  WebhookEndpoint,
  APIKey,
  OrgAPIKey,
  CloudConnection,
  Promotion,
} from '@/lib/types'

export function useProjects() {
  return useApi<Project[]>(() => api.listProjects())
}

// Singleton projects list shared by persistent consumers (the sidebar
// switcher). Call refresh() after a mutation and they all update, no reload.
let sharedProjects: UseApiReturn<Project[]> | null = null
export function useSharedProjects() {
  if (!sharedProjects) {
    sharedProjects = useApi<Project[]>(() => api.listProjects())
  }
  return sharedProjects
}

export function useProjectTree() {
  return useApi<ProjectTree[]>(() => api.listProjectTree())
}

export function useProject(slug: Ref<string> | string) {
  return useReactiveApi<Project>(() => {
    const s = isRef(slug) ? slug.value : slug
    if (!s) return null
    return () => api.getProject(s)
  })
}

export function useFlags(project: Ref<string> | string) {
  return useReactiveApi<FlagWithEnvironments[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listFlags(p)
  })
}

export function useFlag(project: Ref<string> | string, key: Ref<string> | string) {
  return useReactiveApi<FlagWithEnvironments>(() => {
    const p = isRef(project) ? project.value : project
    const k = isRef(key) ? key.value : key
    if (!p || !k) return null
    return () => api.getFlag(p, k)
  })
}

export function useEnvironments(project: Ref<string> | string) {
  return useReactiveApi<Environment[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listEnvironments(p)
  })
}

export function useCloudConnections(project: Ref<string> | string, envName: Ref<string> | string) {
  return useReactiveApi<CloudConnection[]>(() => {
    const p = isRef(project) ? project.value : project
    const e = isRef(envName) ? envName.value : envName
    if (!p || !e) return null
    return () => api.listCloudConnections(p, e)
  })
}

export function useSegments(project: Ref<string> | string) {
  return useReactiveApi<Segment[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listSegments(p)
  })
}

export function useSyncStatus(project: Ref<string> | string) {
  return useReactiveApi<SyncStatus[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.getSyncStatus(p)
  })
}

export function useSchedules(project: Ref<string> | string) {
  return useReactiveApi<Schedule[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listSchedules(p)
  })
}

export function useWebhooks(project: Ref<string> | string) {
  return useReactiveApi<WebhookEndpoint[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listWebhooks(p)
  })
}

export function useOrg() {
  return useApi<import('@/lib/types').OrgDetails>(() => api.getOrg())
}

export function useOrgApiKeys() {
  return useApi<OrgAPIKey[]>(() => api.listOrgApiKeys())
}

export function useApiKeys(project: Ref<string> | string) {
  return useReactiveApi<APIKey[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listApiKeys(p)
  })
}

export function usePromotions(project: Ref<string> | string) {
  return useReactiveApi<Promotion[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listPromotions(p)
  })
}

// Versions and push history deliberately have no composable here. Both are
// paginated, and useReactiveApi replaces `data` wholesale from a zero-arg
// fetcher whose watchEffect re-runs on any reactive read — so there is no way
// to hand it a page number without it clobbering the page you just loaded.
// Versions.vue fetches them with plain refs, as AuditLog.vue does.
